import { useEffect, useState } from "react";
import styled from "styled-components";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow, { tableRowClasses } from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import React from 'react';
import Link from '@mui/material/Link';
import orderBy from 'lodash/orderBy';
import { useLanguage } from '@/app/utils/LanguageContext';
import { useMyContext } from '@/app/utils/MyContext';
import { getMeetingRaceNews } from '@/app/utils/request/requestList';
import { theme } from "@/app/utils/Common";
import { useMediaQuery } from "@mui/material";
import { textSize, text_size } from "@/app/utils/MixinSize";

interface RacingInfoProps {
  listtype?: string;
  meetings: IMeeting[];
  type: string;
  view?: string;
}

export interface Article {
  item: {
    displayName: string;
    publishDateTime: {
      date: string;
      time: string;
      value: string;
    };
    title: {
      value: string;
    };
    target: {
      value: string;
    };
    url: {
      value: string;
    };
  }
}

interface Data {
  publishDateTime: string;
  subject: string;
}
function createData(
  publishDateTime: string,
  subject: string,
): Data {
  return {
    publishDateTime,
    subject
  }
}

const NewsListToggleCell = styled(TableCell)(() => ({
    [`&`]: {
        backgroundColor: "#FFFFFF",
        height: "32px",
        color: "#122C68",
        ...textSize(13),
        fontWeight: "700",
        cursor: "pointer",
        padding: "0px",
        textAlign: "center",
    }
  }));

const TableHeader = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#E1E1E1",
    backgroundColor: "#132B68",
    fontWeight: 500,
    padding: "8px 12px",
    ...textSize(15),
    textAlign: "left",
    height: "35.52px",
    lineHeight: "16px",
  }
}));

const StyledTableCell = styled(TableCell)<{ iscenter?: string }>(({ iscenter }) => ({
  [`&.${tableCellClasses.root}`]: {
    color: "#333333",
  },
  [`&.${tableCellClasses.head}`]: {
    color: "#E1E1E1",
    backgroundColor: "#132B68",
    fontWeight: 500,
    padding: "8px 12px",
    ...textSize(15),
    textAlign: "left",
    height: "35.52px",
    lineHeight: "16px",
  },
  [`&.${tableCellClasses.head}:last-child`]: {
    paddingRight: "8px",
  },
  [`&.${tableCellClasses.body}`]: {
    ...textSize(13),
    fontWeight: 500,
    padding: "8px 12px",
    verticalAlign: "baseline",
    textAlign: iscenter == "true" ? "center" : "left",
    height: "34px",
    lineHeight: "18px"
  },
}));

const StyledTableCellMobile = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    color: "#000000",
  },
  [`&.${tableCellClasses.body}:first-child`]: {
    paddingLeft: "12px",
  },
  [`&.${tableCellClasses.body}:last-child`]: {
    paddingRight: "12px",
  },
  [`&.${tableCellClasses.body}`]: {
    ...textSize(13),
    fontWeight: 500,
    verticalAlign: "baseline",
    padding: "8px 6px",
    textAlign: "left",
    lineHeight: "18px"
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(even)': {
    backgroundColor: "#F4F4F4",
  },
  '&:nth-of-type(odd)': {
    backgroundColor: "#FFFFFF",
  },
  'td, th': {
    border: 0,
  }
}));

const NewsList: React.FC<RacingInfoProps> = (props: RacingInfoProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down("lg")) ?? false;
  const { state: lang } = useLanguage();
  const { t: dict } = lang;
  const [isNewsListExpanded, setIsNewsListExpanded] = React.useState(false);
  const [articleList, setArticleList] = useState<Article[]>([]);
  const { state: myContextState, dispatch: myContextDispatch } = useMyContext();
  var _listtype = (typeof props.listtype === 'undefined') ? "latestNews" : props.listtype;

  useEffect(() => {
    if ((props.view ?? false) && props.view?.toLowerCase() == "race"){
        if (!myContextState.selectedRace?.id) {
            setArticleList([]);
            return
        }
        getMeetingRaceNews(lang.langCode, myContextState.selectedRace?.id.toLowerCase())
          .then((res: any) => {
            setArticleList(orderBy(res.search?.results?.items || [], ['item.publishDateTime.value'], ['desc']));
            setIsNewsListExpanded(res.search?.results?.items.length < 4);
          })
          .catch((e: any) => {
            console.log(e);
          });
    }
    else{
        if (!myContextState.selectedMeeting?.id) {
            setArticleList([]);
            return
        }
        getMeetingRaceNews(lang.langCode, myContextState.selectedMeeting?.id.toLowerCase())
          .then((res: any) => {
            setArticleList(orderBy(res.search?.results?.items || [], ['item.publishDateTime.value'], ['desc']));
            setIsNewsListExpanded(res.search?.results?.items.length < 4);
          })
          .catch((e: any) => {
            console.log(e);
          });
    }
  }, [myContextState.selectedMeeting, myContextState.selectedRace]);

  const newsListViewToggle = () => {
    setIsNewsListExpanded(!isNewsListExpanded);
  }

  const renderPcTable = () => {
    return (
      <TableContainer sx={{ display: "flex", padding: "10px 0px" }}>
        <Table id={_listtype + "_Table_pc"} sx={{ border: "var(--border)", borderCollapse: "separate", borderRadius: "4px", overflow: "hidden", textAlign: "left !important", boxShadow: "0px 2px 10px 0px rgb(0 0 0 / 10%)" }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableHeader id={_listtype + "_Title_pc"} colSpan={3}>{dict(_listtype)}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {articleList?.map((article: Article, i:number) => (
                ((i < 3) || isNewsListExpanded) &&
              <StyledTableRow id={_listtype + "_TableRow_pc_" + i} key={i}>
                  <StyledTableCell sx={{width: "70px"}}>{article.item?.publishDateTime?.date}</StyledTableCell>
                  <StyledTableCell sx={{width: "70px"}}>{article.item?.publishDateTime?.time}</StyledTableCell>
                  <StyledTableCell><Link href={article.item?.url?.value} target={article.item?.target?.value !== 'undefined' && article.item?.target?.value == '02-CurrentPage' ? "_self" : "_blank"} rel="noopener" underline="hover">{article.item?.title?.value}</Link></StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
          <TableFooter>
                <TableRow>
                    {
                        isNewsListExpanded ? <></> :
                        <NewsListToggleCell colSpan={3} id={_listtype + "ExpandBtn"} onClick={newsListViewToggle}>
                            <span style={{ verticalAlign: "middle" }}>{isNewsListExpanded ? dict("viewLess") : dict("viewMore")}</span>
                            <img src={`${myContextState.assetPrefix}/footer/arrow-up.svg`} style={(isNewsListExpanded ? { verticalAlign: "middle", transform: "rotate(0)" } : { verticalAlign: "middle", transform: "rotate(-180deg)" })} />
                        </NewsListToggleCell>
                    }
                </TableRow>
            </TableFooter>
        </Table>
      </TableContainer>
    )
  };

  const renderMobileTable = () => {
    return (
      <TableContainer sx={{ display: "flex", marginTop: "2.37vw", boxShadow: "0px 2px 10px 0px rgb(0 0 0 / 10%)", border: "var(--border)", borderCollapse: "separate", borderRadius: "4px", overflow: "hidden" }}>
        <Table id={_listtype + "_Table_mobile"} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableHeader id={_listtype + "_Title_mobile"} colSpan={3}>{dict(_listtype)}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {articleList?.map((article: any, i:number) => (
                ((i < 3) || isNewsListExpanded) &&
                <StyledTableRow id={_listtype + "_TableRow_mobile_" + i} key={i}>
                    <StyledTableCellMobile sx={{width: "35px"}}>{article.item?.publishDateTime?.date}</StyledTableCellMobile>
                    <StyledTableCellMobile sx={{width: "34px"}}>{article.item?.publishDateTime?.time}</StyledTableCellMobile>
                    <StyledTableCellMobile><Link href={article.item?.url?.value} target={article.item?.target?.value !== 'undefined' && article.item?.target?.value == '02-CurrentPage' ? "_self" : "_blank"} rel="noopener" underline="hover">{article.item?.title?.value}</Link></StyledTableCellMobile>
                </StyledTableRow>
            ))}
          </TableBody>
          <TableFooter>
            {
                isNewsListExpanded ? <></> :
                <NewsListToggleCell colSpan={3} id={_listtype + "ExpandBtn"} onClick={newsListViewToggle}>
                    <span style={{ verticalAlign: "middle" }}>{isNewsListExpanded ? dict("viewLess") : dict("viewMore")}</span>
                    <img src={`${myContextState.assetPrefix}/footer/arrow-up.svg`} style={(isNewsListExpanded ? { verticalAlign: "middle", transform: "rotate(0)" } : { verticalAlign: "middle", transform: "rotate(-180deg)" })} />
                </NewsListToggleCell>
            }
          </TableFooter>
        </Table>
      </TableContainer>
    )
  };

  return (
    <>
      {articleList.length > 0 && (props?.type === "pc" ? renderPcTable() : renderMobileTable())}
    </>
  );
};
export default NewsList;
